// Check if expression uses exactly the provided numbers in order
export function validateNumberUsage(
    expression: string,
    numbers: number[]
): { valid: boolean; error?: string } {
    // Extract all digits from the expression in order
    const digitsInExpression = expression.match(/\d/g)?.map(Number) || [];

    if (digitsInExpression.length !== numbers.length) {
        return { valid: false, error: "You must use all numbers exactly once" };
    }

    // Check if digits appear in the same order as provided numbers
    for (let i = 0; i < numbers.length; i++) {
        if (numbers[i] !== digitsInExpression[i]) {
            return {
                valid: false,
                error: "You must use the numbers in the order provided",
            };
        }
    }

    return { valid: true };
}

// Factorial function
function factorial(n: number): number {
    if (n < 0 || !Number.isInteger(n)) {
        throw new Error("Factorial is only defined for non-negative integers");
    }
    if (n === 0 || n === 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
        result *= i;
    }
    return result;
}

// Multi-factorial function (double factorial, triple factorial, etc.)
// For example: 6!! = 6*4*2, 5!! = 5*3*1, 7!!! = 7*4*1
function multifactorial(n: number, step: number): number {
    if (n < 0 || !Number.isInteger(n) || step < 1 || !Number.isInteger(step)) {
        throw new Error("Invalid arguments for multifactorial");
    }
    if (n === 0 || n === 1) return 1;
    let result = 1;
    for (let i = n; i > 0; i -= step) {
        result *= i;
    }
    return result;
}

// Safe evaluation of mathematical expressions
export function evaluateExpression(expression: string): {
    result?: number;
    error?: string;
} {
    try {
        // Remove all whitespace
        let expr = expression.replace(/\s+/g, "");

        // Check for invalid characters
        if (!/^[\d+\-*/().!^]+$/.test(expr)) {
            return {
                error: "Invalid characters in expression. Only numbers and +, -, *, /, ^, !, () are allowed",
            };
        }

        // Replace ^ with ** for exponentiation
        expr = expr.replace(/\^/g, "**");

        // Handle factorials (process from right to left)
        while (expr.includes("!")) {
            // Find the rightmost factorial with all exclamation marks
            const factorialMatch = expr.match(/(\d+)(!+)/);
            if (!factorialMatch) break;

            const num = parseInt(factorialMatch[1]);
            const exclamationCount = factorialMatch[2].length;

            let factValue: number;
            if (exclamationCount === 1) {
                // Regular factorial: n!
                factValue = factorial(num);
            } else {
                // Multi-factorial: n!!, n!!!, etc.
                factValue = multifactorial(num, exclamationCount);
            }

            expr = expr.replace(factorialMatch[0], factValue.toString());
        }

        // Evaluate the expression using Function constructor (safer than eval)
        // eslint-disable-next-line no-new-func
        const result = Function(`'use strict'; return (${expr})`)();

        if (typeof result !== "number" || !isFinite(result)) {
            return { error: "Expression resulted in an invalid number" };
        }

        return { result };
    } catch (error) {
        return { error: "Invalid expression. Please check your syntax" };
    }
}

// Check if the result equals the target (with small tolerance for floating point)
export function checkResult(
    result: number,
    target: number = 10,
    tolerance: number = 0.0001
): boolean {
    return Math.abs(result - target) < tolerance;
}
